import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import SEO from '../../components/SEO';

const TermsOfServicePage = () => {
  const lastUpdated = 'February 16, 2026';
  const companyName = 'Smart POS';
  const companyEmail = 'support@smartpos.com';

  return (
    <div className="min-h-screen bg-primaryB-50">
      <SEO
        title="Terms of Service"
        description="Terms of Service for Smart POS – Point of Sale and business management software for African businesses."
        path="/terms"
      />
      <LandingNav />

      <section className="pt-24 sm:pt-32 pb-12">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold mb-6 transition-all duration-200 text-sm"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
            Back to Home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-primaryB-900 mb-2 tracking-tight">Terms of Service</h1>
          <p className="text-primaryB-300 text-sm">Last updated: {lastUpdated}</p>
        </div>
      </section>

      <section className="pb-16 sm:pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="bg-primaryB-50 rounded-3xl border border-primaryB-200/60 p-6 sm:p-10">
            <div className="space-y-6 text-primaryB-700 text-sm leading-relaxed">

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">1. Acceptance of Terms</h2>
                <p>By accessing or using {companyName} ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">2. Description of Service</h2>
                <p className="mb-3">{companyName} provides a cloud-based point of sale and business management platform designed for retail stores, restaurants, salons, and other businesses. The Service includes features such as:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Point of sale processing</li>
                  <li>Inventory management</li>
                  <li>Staff management and role-based access</li>
                  <li>Customer relationship management</li>
                  <li>Sales analytics and reporting</li>
                  <li>Daily email reports</li>
                  <li>Multi-branch management</li>
                </ul>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">3. Account Registration</h2>
                <p className="mb-3">To use the Service, you must register for an account. You agree to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Provide accurate and complete registration information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorized access</li>
                  <li>Accept responsibility for all activities under your account</li>
                </ul>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">4. Subscription and Payment</h2>
                <p className="mb-3">The Service is offered on a subscription basis. By subscribing, you agree to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Pay all applicable fees for your selected plan</li>
                  <li>Provide valid payment information</li>
                  <li>Authorize automatic renewal unless cancelled</li>
                </ul>
                <p className="mt-3">We offer a 1 month free trial for new users. After the trial period, your account will require an active subscription to continue using the Service.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">5. Acceptable Use</h2>
                <p className="mb-3">You agree not to use the Service to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe on intellectual property rights</li>
                  <li>Transmit malicious code or interfere with the Service</li>
                  <li>Attempt to gain unauthorized access to other accounts</li>
                  <li>Use the Service for fraudulent purposes</li>
                  <li>Resell or redistribute the Service without authorization</li>
                </ul>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">6. Data Ownership</h2>
                <p>You retain ownership of all data you input into the Service ("Your Data"). You grant us a limited license to use Your Data solely for the purpose of providing the Service to you. We will not sell or share Your Data with third parties except as necessary to provide the Service or as required by law.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">7. Service Availability</h2>
                <p className="mb-3">We strive to maintain high availability of the Service but do not guarantee uninterrupted access. The Service may be temporarily unavailable due to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Scheduled maintenance (with advance notice when possible)</li>
                  <li>Emergency repairs</li>
                  <li>Factors beyond our control</li>
                </ul>
                <p className="mt-3">The Service includes offline functionality to help you continue operations during connectivity issues.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">8. Limitation of Liability</h2>
                <p>To the maximum extent permitted by law, {companyName} shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities, arising from your use of the Service.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">9. Indemnification</h2>
                <p>You agree to indemnify and hold harmless {companyName} and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service or violation of these Terms.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">10. Termination</h2>
                <p className="mb-3">We may suspend or terminate your access to the Service if you violate these Terms or fail to pay applicable fees. Upon termination:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Your access to the Service will be revoked</li>
                  <li>You may request export of Your Data within 30 days</li>
                  <li>Outstanding fees remain payable</li>
                </ul>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">11. Changes to Terms</h2>
                <p>We may update these Terms from time to time. We will notify you of significant changes via email or through the Service. Your continued use of the Service after changes take effect constitutes acceptance of the updated Terms.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">12. Governing Law</h2>
                <p>These Terms shall be governed by and construed in accordance with the laws of Ghana. Any disputes arising from these Terms or the Service shall be resolved in the courts of Ghana.</p>
              </div>

              <div>
                <h2 className="text-lg font-bold text-primaryB-900 mb-2">13. Contact Information</h2>
                <p>If you have questions about these Terms, please contact us at: <strong className="text-primaryB-900">{companyEmail}</strong></p>
              </div>

            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default TermsOfServicePage;
